import analytics from './analytics';
import { throttle } from 'lodash';
//based on https://github.com/robflaherty/scroll-depth and https://github.com/leighmcculloch/gascrolldepth.js

function getDocumentHeight() {
  return Math.max(
    document.documentElement['scrollHeight'],
    document.body['scrollHeight'],
    document.documentElement['offsetHeight'],
    document.body['offsetHeight'],
    document.documentElement['clientHeight']
  );
}

const THRESHOLDS = [
  { label: '25%', value: 0.25 },
  { label: '50%', value: 0.5 },
  { label: '75%', value: 0.75 },
  { label: '90%', value: 0.9 },
];

const triggeredThresholds = new Set();
let scrollEventBound = false;
let lastScrollY = 0;

function sendScrollEvent(label: string) {
  analytics.trackCustomEvent('scroll', { event_label: label });
}

function checkMarks(scrollPercent: number) {
  THRESHOLDS.forEach(({ label, value }) => {
    if (!triggeredThresholds.has(label) && scrollPercent >= value) {
      sendScrollEvent(label);
      triggeredThresholds.add(label);
    }
  });
}

/*
 * Public Methods
 */

export function resetMarks() {
  triggeredThresholds.clear();
  // Baseline on the current position so residual scroll left over from the
  // previous page (e.g. before scroll-to-top settles) is not counted as the
  // user scrolling down into the new page.
  lastScrollY = getPageYOffset();
  bindScrollDepth();
}

/*
 * Scroll Event
 */
const onScroll = throttle(function () {
  /*
   * We calculate document and window height on each scroll event to
   * account for dynamic DOM changes.
   */

  const docHeight = getDocumentHeight();
  const winHeight = getWindowHeight();
  const scrollableDistance = docHeight - winHeight;
  const scrollY = getPageYOffset();
  const scrolledDown = scrollY > lastScrollY;
  lastScrollY = scrollY;

  // If all marks already hit, unbind scroll event
  if (triggeredThresholds.has('90%')) {
    window.removeEventListener('scroll', onScroll);
    scrollEventBound = false;
    return;
  }

  // Page fits within the viewport: nothing to track.
  if (scrollableDistance <= 0) {
    return;
  }

  // Only count depth the user physically scrolls down to. Upward scrolls,
  // scroll-to-top, and layout shifts from content loading must not fire events.
  if (!scrolledDown) {
    return;
  }

  const scrollPercent = scrollY / scrollableDistance;
  checkMarks(scrollPercent);
}, 500);

export function bindScrollDepth() {
  if (scrollEventBound) {
    return;
  }
  scrollEventBound = true;
  window.addEventListener('scroll', onScroll);
}

function getWindowHeight() {
  return window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
}
function getPageYOffset() {
  return (
    window.scrollY ||
    window.pageYOffset ||
    (document.compatMode === 'CSS1Compat' ? document.documentElement.scrollTop : document.body.scrollTop)
  );
}
